import React, { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Eye, EyeOff, Pencil, X, BadgeCheck, FlaskConical } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";

const BLANK = { name: "", photo: "", intro: "", qualifications: [], subjects: [], languages: [], year_levels: [], location: "", mode: "Online", rate: 0, availability: "", rating: null, verified: false, sample: false, visible: true, email: "" };
const MODES = ["Online", "In-person", "Online & In-person"];
const asList = (s) => s.split(",").map((p) => p.trim()).filter(Boolean);

export function TutorForm({ initial, onCancel, onSaved }) {
  const [f, setF] = useState({ ...BLANK, ...initial });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const setL = (k) => (e) => setF((p) => ({ ...p, [k]: asList(e.target.value) }));
  const editing = Boolean(initial?.id);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const payload = {
        name: f.name.trim(), photo: f.photo.trim(), intro: f.intro.trim(),
        qualifications: f.qualifications, subjects: f.subjects, languages: f.languages,
        year_levels: f.year_levels, location: f.location.trim(), mode: f.mode,
        rate: Number(f.rate) || 0, availability: f.availability.trim(),
        rating: f.rating === "" || f.rating === null ? null : Number(f.rating),
        verified: !!f.verified, sample: !!f.sample, visible: f.visible !== false, email: f.email || "",
      };
      if (editing) await api.put(`/admin/tutors/${initial.id}`, payload);
      else await api.post("/admin/tutors", payload);
      toast.success(editing ? "Tutor updated" : `${payload.name} is now live in the marketplace`);
      onSaved();
    } catch {
      toast.error("Could not save that tutor. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-teal-200 bg-teal-50/40 p-5" data-testid="tutor-form">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Name</Label><Input value={f.name} onChange={set("name")} data-testid="tutor-name-input" /></div>
          <div><Label>Location</Label><Input value={f.location} onChange={set("location")} placeholder="Melbourne, VIC" data-testid="tutor-location-input" /></div>
        </div>
        <div>
          <Label>Photo URL</Label>
          <Input value={f.photo} onChange={set("photo")} placeholder="Paste a link, or leave the applicant's uploaded headshot" data-testid="tutor-photo-input" />
          {f.photo && <img src={f.photo} alt="" className="mt-2 w-24 h-24 rounded-xl object-cover border border-slate-200" data-testid="tutor-photo-preview" />}
        </div>
        <div><Label>Short intro <span className="text-slate-400">(families read this first)</span></Label><Textarea rows={3} value={f.intro} onChange={set("intro")} data-testid="tutor-intro-input" /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Subjects <span className="text-slate-400">(comma separated)</span></Label><Input value={f.subjects.join(", ")} onChange={setL("subjects")} data-testid="tutor-subjects-input" /></div>
          <div><Label>Languages <span className="text-slate-400">(comma separated)</span></Label><Input value={f.languages.join(", ")} onChange={setL("languages")} data-testid="tutor-languages-input" /></div>
          <div><Label>Year levels <span className="text-slate-400">(comma separated)</span></Label><Input value={f.year_levels.join(", ")} onChange={setL("year_levels")} placeholder="Primary, Year 7-9" data-testid="tutor-yearlevels-input" /></div>
          <div><Label>Qualifications <span className="text-slate-400">(comma separated)</span></Label><Input value={f.qualifications.join(", ")} onChange={setL("qualifications")} data-testid="tutor-quals-input" /></div>
          <div>
            <Label>Delivery</Label>
            <select value={f.mode} onChange={set("mode")} className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="tutor-mode-input">
              {MODES.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div><Label>Hourly rate (AUD)</Label><Input type="number" value={f.rate} onChange={set("rate")} data-testid="tutor-rate-input" /></div>
        </div>
        <div><Label>Availability</Label><Input value={f.availability} onChange={set("availability")} placeholder="Weeknights & weekends" data-testid="tutor-availability-input" /></div>
        <div className="flex flex-wrap gap-5 text-sm text-slate-700">
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.verified} onChange={(e) => setF((p) => ({ ...p, verified: e.target.checked }))} data-testid="tutor-verified-input" /> Verified (clearance sighted)</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={f.visible !== false} onChange={(e) => setF((p) => ({ ...p, visible: e.target.checked }))} data-testid="tutor-visible-input" /> Show in marketplace</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.sample} onChange={(e) => setF((p) => ({ ...p, sample: e.target.checked }))} data-testid="tutor-sample-input" /> Sample profile</label>
        </div>
        <p className="text-xs text-slate-500">Leave the rating empty until this tutor has real reviews — the profile will show a "New tutor" badge instead.</p>
        <div><Label>Rating <span className="text-slate-400">(optional)</span></Label><Input type="number" step="0.1" min="0" max="5" value={f.rating ?? ""} onChange={set("rating")} className="max-w-[120px]" data-testid="tutor-rating-input" /></div>
        <div className="flex gap-2">
          <Button type="submit" disabled={saving} className="bg-teal-600 hover:bg-teal-700 rounded-full" data-testid="tutor-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {editing ? "Save changes" : "Publish tutor"}
          </Button>
          <Button type="button" variant="outline" className="rounded-full" onClick={onCancel} data-testid="tutor-cancel-btn"><X className="w-4 h-4" /> Cancel</Button>
        </div>
      </form>
    </Card>
  );
}

export default function TutorsPanel({ draft, onDraftHandled }) {
  const [tutors, setTutors] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/tutors").then((r) => setTutors(r.data)).catch(() => setTutors([]));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (draft) setEditing(draft); }, [draft]);

  const close = () => { setEditing(null); onDraftHandled?.(); };

  const save = async (t, patch) => {
    const { id, order, created_at, ...rest } = t;
    await api.put(`/admin/tutors/${t.id}`, { ...rest, ...patch });
    load();
  };

  const remove = async (t) => {
    if (!window.confirm(`Remove ${t.name} from the marketplace?`)) return;
    await api.delete(`/admin/tutors/${t.id}`);
    toast.success(`${t.name} removed`);
    load();
  };

  if (!tutors) return <Loader2 className="w-5 h-5 animate-spin text-teal-600" data-testid="tutors-panel-loading" />;

  return (
    <div className="space-y-4" data-testid="tutors-panel">
      {editing ? (
        <TutorForm initial={editing} onCancel={close} onSaved={() => { close(); load(); }} />
      ) : (
        <Button onClick={() => setEditing(BLANK)} className="bg-teal-600 hover:bg-teal-700 rounded-full" data-testid="tutor-add-btn">
          <Plus className="w-4 h-4" /> Add a tutor
        </Button>
      )}

      <div className="space-y-3">
        {tutors.map((t) => (
          <Card key={t.id} className="border-slate-200 p-4" data-testid={`admin-tutor-${t.id}`}>
            <div className="flex items-start gap-4">
              {t.photo
                ? <img src={t.photo} alt="" className="w-16 h-16 rounded-xl object-cover border border-slate-200 shrink-0" data-testid={`admin-tutor-photo-${t.id}`} />
                : <div className="w-16 h-16 rounded-xl bg-slate-100 shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-slate-900">{t.name}</p>
                  {t.verified && <Badge className="bg-teal-50 text-teal-700 hover:bg-teal-50 text-[10px]"><BadgeCheck className="w-3 h-3 mr-1" /> Verified</Badge>}
                  {t.sample && <Badge variant="secondary" className="text-[10px]"><FlaskConical className="w-3 h-3 mr-1" /> Sample</Badge>}
                  {t.visible === false && <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200 text-[10px]">Hidden</Badge>}
                </div>
                <p className="text-sm text-slate-600 mt-1 line-clamp-2">{t.intro}</p>
                <p className="text-xs text-slate-500 mt-1">{[t.location, (t.subjects || []).join(", "), t.rate ? `$${t.rate}/hr` : null].filter(Boolean).join(" · ")}</p>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEditing(t)} data-testid={`tutor-edit-${t.id}`}><Pencil className="w-4 h-4" /> Edit</Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => save(t, { visible: t.visible === false })} data-testid={`tutor-toggle-${t.id}`}>
                  {t.visible === false ? <><Eye className="w-4 h-4" /> Show</> : <><EyeOff className="w-4 h-4" /> Hide</>}
                </Button>
                <Button size="sm" variant="outline" className="rounded-full text-red-600 hover:text-red-700" onClick={() => remove(t)} data-testid={`tutor-delete-${t.id}`}><Trash2 className="w-4 h-4" /> Delete</Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
